/**
 * Punto de entrada del widget: `SeatsAI.render({...})`.
 * Se compila a un IIFE con el global `SeatsAI` (scripts/build-widget.ts).
 */
import { isSessionToken } from "./session.js";
import { Widget } from "./widget.js";
import type { SeatsOptions, SeatsWidget } from "./types.js";

export type * from "./types.js";

export const version = "0.1.0";

/** Origen desde el que se cargó este script: base por defecto de la API. Se captura al evaluar el script. */
const scriptOrigin: string = (() => {
  try {
    const src = (document.currentScript as HTMLScriptElement | null)?.src;
    return src ? new URL(src).origin : "";
  } catch {
    return "";
  }
})();

const instances = new WeakMap<HTMLElement, Widget>();

export function render(options: SeatsOptions): SeatsWidget {
  if (!options || typeof options !== "object") throw new TypeError("SeatsAI.render: faltan las opciones");
  const el = options.el;
  const eventId = options.eventId;
  const publicKey = options.publicKey;
  if (!(el instanceof HTMLElement)) throw new TypeError("SeatsAI.render: `el` debe ser un HTMLElement");
  if (typeof eventId !== "string" || !eventId) throw new TypeError("SeatsAI.render: `eventId` es obligatorio");
  if (typeof publicKey !== "string" || !publicKey) throw new TypeError("SeatsAI.render: `publicKey` es obligatoria");
  if (publicKey.startsWith("sk_")) {
    throw new TypeError("SeatsAI.render: nunca uses una clave secreta (sk_...) en el navegador; usa la clave pública (pk_...).");
  }
  const token = options.sessionToken;
  if (token !== undefined && token !== "" && !isSessionToken(token)) {
    throw new TypeError("SeatsAI.render: `sessionToken` no es válido (usa el token ses_... de tu servidor).");
  }
  instances.get(el)?.destroy(); // un contenedor, un widget
  const baseUrl = (options.baseUrl ?? scriptOrigin).replace(/\/+$/, "");
  const w = new Widget(options, baseUrl);
  instances.set(el, w);
  return w;
}
